import { useState } from 'react'
import { loadTeamSheetFonts } from '../utils/generateTeamSheet'

function arrayBufferToBase64(buffer) {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d)) return ''
  return d.toLocaleDateString('en-IE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
}

function formatScore(f) {
  if (f.status !== 'completed') return ''
  return f.home_goals + '-' + String(f.home_points).padStart(2, '0') + ' v ' + f.away_goals + '-' + String(f.away_points).padStart(2, '0')
}

function getResult(f) {
  if (f.status !== 'completed') return ''
  const home = f.home_goals * 3 + f.home_points
  const away = f.away_goals * 3 + f.away_points
  if (home > away) return 'W'
  if (home < away) return 'L'
  return 'D'
}

function gameTypeLabel(type) {
  if (type === 'league') return 'League'
  if (type === 'championship') return 'Championship'
  if (type === 'cup') return 'Cup'
  if (type === 'friendly') return 'Friendly'
  return type || ''
}

export default function FixturesExportModal({ fixtures, squadName, clubName, season, profile, onClose }) {
  const [format, setFormat] = useState('pdf')
  const [emailSelf, setEmailSelf] = useState(false)
  const [freeTextEmail, setFreeTextEmail] = useState('')
  const [generating, setGenerating] = useState(false)
  const [emailState, setEmailState] = useState(null)
  const [emailError, setEmailError] = useState('')

  const upcoming = fixtures.filter(f => f.status !== 'completed')
  const completed = fixtures.filter(f => f.status === 'completed')
  const wins = completed.filter(f => getResult(f) === 'W').length
  const losses = completed.filter(f => getResult(f) === 'L').length
  const draws = completed.filter(f => getResult(f) === 'D').length

  // ── CSV export ─────────────────────────────────────────────────────────────
  function exportCSV() {
    const headers = ['Date', 'Opponent', 'Sport', 'Team', 'Competition', 'Venue', 'Result', 'Score']
    const rows = [...completed, ...upcoming].map(f => [
      formatDate(f.fixture_date),
      f.opponent,
      f.sport ? f.sport.charAt(0).toUpperCase() + f.sport.slice(1) : 'Football',
      f.team_number ? 'Team ' + f.team_number : '',
      gameTypeLabel(f.game_type),
      f.venue || '',
      f.status === 'completed' ? getResult(f) : 'Upcoming',
      formatScore(f)
    ])
    const csv = [headers, ...rows].map(r => r.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = squadName + ' - Fixtures ' + (season || '') + '.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // ── PDF export ─────────────────────────────────────────────────────────────
  async function generatePDF() {
    const { PDFDocument, rgb: pdfRgb } = await import('pdf-lib')
    const fontkit = (await import('@pdf-lib/fontkit')).default
    const { fontRegular, fontBold } = await loadTeamSheetFonts()

    const PAGE_W = 595, PAGE_H = 842
    const MARGIN = 36
    const BLUE = pdfRgb(0.094, 0.373, 0.647)
    const GREY = pdfRgb(0.45, 0.45, 0.45)
    const LIGHT_GREY = pdfRgb(0.85, 0.85, 0.85)
    const GREEN = pdfRgb(0.23, 0.43, 0.07)
    const RED = pdfRgb(0.64, 0.18, 0.18)
    const BLACK = pdfRgb(0.1, 0.1, 0.1)

    const pdfDoc = await PDFDocument.create()
    pdfDoc.registerFontkit(fontkit)
    const font = await pdfDoc.embedFont(fontRegular)
    const fontB = await pdfDoc.embedFont(fontBold)

    const pages = []
    let page, y

    function addPage() {
      page = pdfDoc.addPage([PAGE_W, PAGE_H])
      pages.push(page)
      y = PAGE_H - MARGIN
    }
    addPage()

    function text(str, x, yy, opts = {}) {
      page.drawText(str || '', { x, y: yy, size: opts.size || 9, font: opts.bold ? fontB : font, color: opts.color || BLACK })
    }

    // Header
    text(clubName || 'Club', MARGIN, y, { size: 18, bold: true, color: BLUE })
    y -= 22
    text(squadName, MARGIN, y, { size: 13, bold: true })
    text('Season ' + (season || ''), PAGE_W - MARGIN - 80, y, { size: 11, color: GREY })
    y -= 20
    page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 0.6, color: LIGHT_GREY })
    y -= 18

    // Season summary
    if (completed.length > 0) {
      text('Season Summary', MARGIN, y, { size: 11, bold: true })
      y -= 16
      const summaryItems = [
        { label: 'Played', value: String(completed.length) },
        { label: 'Won', value: String(wins), color: GREEN },
        { label: 'Lost', value: String(losses), color: RED },
        { label: 'Drawn', value: String(draws), color: GREY },
      ]
      let sx = MARGIN
      summaryItems.forEach(item => {
        text(item.label, sx, y, { size: 8, color: GREY })
        text(item.value, sx, y - 13, { size: 16, bold: true, color: item.color || BLACK })
        sx += 70
      })
      y -= 36
      page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 0.5, color: LIGHT_GREY })
      y -= 16
    }

    // Column headers
    const cols = { date: MARGIN, opponent: MARGIN + 68, sport: MARGIN + 218, team: MARGIN + 268, comp: MARGIN + 298, venue: MARGIN + 358, result: MARGIN + 418, score: MARGIN + 444 }
    text('Date', cols.date, y, { size: 8, bold: true, color: GREY })
    text('Opponent', cols.opponent, y, { size: 8, bold: true, color: GREY })
    text('Sport', cols.sport, y, { size: 8, bold: true, color: GREY })
    text('Team', cols.team, y, { size: 8, bold: true, color: GREY })
    text('Competition', cols.comp, y, { size: 8, bold: true, color: GREY })
    text('Venue', cols.venue, y, { size: 8, bold: true, color: GREY })
    text('R', cols.result, y, { size: 8, bold: true, color: GREY })
    text('Score', cols.score, y, { size: 8, bold: true, color: GREY })
    y -= 12
    page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 0.5, color: LIGHT_GREY })
    y -= 14

    // All fixtures sorted by date
    const sorted = [...fixtures].sort((a, b) => new Date(a.fixture_date) - new Date(b.fixture_date))
    sorted.forEach((f, i) => {
      if (y < 80) {
        page = pdfDoc.addPage([PAGE_W, PAGE_H])
        pages.push(page)
        y = PAGE_H - MARGIN
      }
      const result = getResult(f)
      const resultColor = result === 'W' ? GREEN : result === 'L' ? RED : result === 'D' ? GREY : GREY
      const rowBg = i % 2 === 0 ? pdfRgb(0.98, 0.98, 0.98) : pdfRgb(1, 1, 1)
      page.drawRectangle({ x: MARGIN, y: y - 5, width: PAGE_W - MARGIN * 2, height: 16, color: rowBg })
      text(formatDate(f.fixture_date), cols.date, y, { size: 8 })
      text(f.opponent, cols.opponent, y, { size: 8 })
      text(f.sport ? f.sport.charAt(0).toUpperCase() + f.sport.slice(1) : 'Football', cols.sport, y, { size: 8, color: GREY })
      text(f.team_number ? 'T' + f.team_number : '', cols.team, y, { size: 8, color: GREY })
      text(gameTypeLabel(f.game_type), cols.comp, y, { size: 8, color: GREY })
      text(f.venue || '', cols.venue, y, { size: 8, color: GREY })
      text(f.status === 'completed' ? result : '—', cols.result, y, { size: 8, bold: true, color: resultColor })
      text(formatScore(f) || (f.status !== 'completed' ? 'Upcoming' : ''), cols.score, y, { size: 8 })
      y -= 16
    })

    // Footer
    pages.forEach((pg, i) => {
      pg.drawLine({ start: { x: MARGIN, y: 50 }, end: { x: PAGE_W - MARGIN, y: 50 }, thickness: 0.5, color: LIGHT_GREY })
      pg.drawText('Generated via GAA Match Tracker', { x: MARGIN, y: 30, size: 7, font, color: GREY })
      pg.drawText('Page ' + (i + 1) + ' of ' + pages.length, { x: PAGE_W - MARGIN - 60, y: 30, size: 7, font, color: GREY })
    })

    return await pdfDoc.save()
  }

  async function handleDownload() {
    setGenerating(true)
    try {
      if (format === 'csv') {
        exportCSV()
      } else {
        const pdfBytes = await generatePDF()
        const blob = new Blob([pdfBytes], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = squadName + ' - Fixtures ' + (season || '') + '.pdf'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
    } catch (e) {
      alert('Export failed: ' + e.message)
    }
    setGenerating(false)
  }

  async function handleEmail() {
    const recipients = []
    if (emailSelf && profile?.email) recipients.push(profile.email)
    const trimmed = freeTextEmail.trim()
    if (trimmed) recipients.push(trimmed)
    if (recipients.length === 0) { setEmailError('Add at least one recipient.'); return }

    setEmailState('sending')
    setEmailError('')
    try {
      let attachmentBase64 = null, fileName = '', contentType = 'application/pdf'
      if (format === 'pdf') {
        const pdfBytes = await generatePDF()
        attachmentBase64 = arrayBufferToBase64(pdfBytes)
        fileName = squadName + ' - Fixtures ' + (season || '') + '.pdf'
      } else {
        const headers = ['Date', 'Opponent', 'Sport', 'Team', 'Competition', 'Venue', 'Result', 'Score']
        const rows = [...completed, ...upcoming].map(f => [
          formatDate(f.fixture_date), f.opponent,
          f.sport ? f.sport.charAt(0).toUpperCase() + f.sport.slice(1) : 'Football',
          f.team_number ? 'Team ' + f.team_number : '',
          gameTypeLabel(f.game_type),
          f.venue || '', f.status === 'completed' ? getResult(f) : 'Upcoming', formatScore(f)
        ])
        const csv = [headers, ...rows].map(r => r.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(',')).join('\n')
        attachmentBase64 = btoa(unescape(encodeURIComponent(csv)))
        fileName = squadName + ' - Fixtures ' + (season || '') + '.csv'
        contentType = 'text/csv'
      }

      const toField = recipients.length === 1 ? recipients[0] : recipients
      const res = await fetch('https://mrjtwgvscgcvuecxjcbg.supabase.co/functions/v1/send-invite-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.REACT_APP_SUPABASE_ANON_KEY },
        body: JSON.stringify({
          type: 'team_sheet',
          toEmails: toField,
          subject: squadName + ' — Fixtures & Results ' + (season || ''),
          htmlBody: '<div style="font-family: sans-serif; max-width: 480px; margin: 0 auto;"><div style="background: #185FA5; padding: 24px; border-radius: 12px 12px 0 0; text-align: center;"><h1 style="color: white; margin: 0; font-size: 20px;">Fixtures & Results</h1></div><div style="background: white; padding: 28px; border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 12px 12px;"><p style="color: #555;"><strong>' + squadName + '</strong> — Season ' + (season || '') + '</p><p style="color: #888; font-size: 13px;">See attached for the full fixtures and results list.</p></div></div>',
          pdfBase64: attachmentBase64,
          fileName
        })
      })
      if (!res.ok) throw new Error('Failed to send')
      setEmailState('sent')
    } catch (e) {
      setEmailState('error')
      setEmailError(e.message)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: '#f5f5f5', borderRadius: '16px', width: '100%', maxWidth: '480px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #0d3d6b 0%, #185FA5 100%)', color: 'white', padding: '18px 16px' }}>
          <p style={{ fontSize: '11px', opacity: 0.7, letterSpacing: '1px', marginBottom: '4px', textAlign: 'center' }}>EXPORT</p>
          <p style={{ fontSize: '16px', fontWeight: '700', textAlign: 'center' }}>{squadName}</p>
          <p style={{ fontSize: '12px', opacity: 0.8, textAlign: 'center' }}>
            {fixtures.length} fixtures · {completed.length} results ({wins}W {draws}D {losses}L)
          </p>
        </div>

        <div style={{ padding: '14px 12px 0' }}>

          {/* Format selector */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', letterSpacing: '0.5px', marginBottom: '10px' }}>FORMAT</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setFormat('pdf')}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1.5px solid ' + (format === 'pdf' ? '#185FA5' : '#e0e0e0'), background: format === 'pdf' ? '#E6F1FB' : 'white', color: format === 'pdf' ? '#185FA5' : '#888', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                📄 PDF
              </button>
              <button onClick={() => setFormat('csv')}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1.5px solid ' + (format === 'csv' ? '#185FA5' : '#e0e0e0'), background: format === 'csv' ? '#E6F1FB' : 'white', color: format === 'csv' ? '#185FA5' : '#888', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                📊 CSV / Excel
              </button>
            </div>
          </div>

          {/* Download */}
          <button onClick={handleDownload} disabled={generating}
            style={{ display: 'block', width: '100%', padding: '12px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', opacity: generating ? 0.7 : 1, marginBottom: '10px' }}>
            {generating ? 'Generating...' : '⬇ Download ' + format.toUpperCase()}
          </button>

          {/* Email section */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '14px', marginBottom: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', letterSpacing: '0.5px', marginBottom: '10px' }}>EMAIL EXPORT</p>

            {emailState === 'sent' ? (
              <p style={{ fontSize: '13px', color: '#3B6D11', textAlign: 'center', padding: '8px' }}>✅ Sent successfully</p>
            ) : (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={emailSelf} onChange={e => setEmailSelf(e.target.checked)} />
                  <span>Email to myself <span style={{ color: '#aaa', fontSize: '11px' }}>({profile?.email})</span></span>
                </label>
                <div style={{ marginTop: '8px' }}>
                  <p style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Other email address (optional)</p>
                  <input type="email" placeholder="e.g. county board secretary" value={freeTextEmail}
                    onChange={e => setFreeTextEmail(e.target.value)}
                    style={{ display: 'block', width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }} />
                </div>
                {emailError && <p style={{ fontSize: '12px', color: '#A32D2D', marginTop: '8px' }}>{emailError}</p>}
                <button onClick={handleEmail} disabled={emailState === 'sending'}
                  style={{ display: 'block', width: '100%', padding: '11px', marginTop: '12px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', opacity: emailState === 'sending' ? 0.7 : 1 }}>
                  {emailState === 'sending' ? 'Sending...' : '📨 Send ' + format.toUpperCase()}
                </button>
              </>
            )}
          </div>
        </div>

        <div style={{ padding: '0 12px 16px' }}>
          <button onClick={onClose} style={{ width: '100%', padding: '12px', background: 'white', color: '#185FA5', border: '0.5px solid #185FA5', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}